/**
 * Analytics module access hook (Sprint 209 — ISS-330 / REQ-ANL-002).
 *
 * Consumes the fail-closed visibility endpoint GET /auth/modules/analytics/access
 * and drives the visibility of the analytics entry points (dashboard quick
 * action and sidebar entry). Any error or missing data is treated as DENIED
 * (SEC-003: never show the entry point without a real permission).
 */

import { useQuery } from '@tanstack/react-query';
import { rawApi } from '@/lib/api-client';

export interface ModuleAccessResponse {
  module: string;
  permission: string;
  granted: boolean;
}

export function useAnalyticsAccess() {
  return useQuery({
    queryKey: ['module-access', 'analytics'],
    queryFn: async () => {
      const response = await rawApi.get<ModuleAccessResponse>(
        'auth/modules/analytics/access'
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Resolve the visibility flag from the hook payload. Undefined (loading or
 * error) resolves to false — fail-closed.
 */
export function hasAnalyticsAccess(
  data: ModuleAccessResponse | undefined
): boolean {
  return data?.granted === true;
}
